import VueFormGenerator from 'vue-form-generator';


const validBool = (value, fallback) => (typeof value === 'boolean' ? value : (fallback || false));
const resolveCols = value => `col-md-${value || 12}`;
const resolveValidator = (validator, fallback, ...args) => (
  typeof validator === 'function' ?
    validator(...args) :
    VueFormGenerator.validators[fallback](...args)
);

export default {
  submitBtn: (text, hint, onSubmit, classes) => {
    const submit = {
      type: 'submit',
      buttonText: text,
      validateBeforeSubmit: true,
      styleClasses: classes || 'col-md-12 submit-btn-container raised-submit-btn',
    };

    if (hint) {
      submit.hint = hint;
    }

    if (onSubmit) {
      submit.onSubmit = onSubmit;
    }

    return submit;
  },

  textArea: (label, model, placeholder, required, max, cols, customValidator, isVisible) => ({
    type: 'fancyTextArea',
    label,
    model,
    placeholder,
    required,
    max,
    rows: 3,
    styleClasses: resolveCols(cols),
    validator(...args) {
      return resolveValidator(customValidator, 'string', ...args);
    },
    visible(...args) {
      if (isVisible) {
        return typeof isVisible === 'function' ? isVisible(...args) : isVisible;
      }

      return true;
    },
  }),

  simpleInput: (
    inputType,
    label,
    placeholder,
    model,
    required,
    cols,
    customValidator,
    isVisible,
  ) => ({
    type: 'input',
    inputType,
    label,
    placeholder,
    model,
    required,
    styleClasses: resolveCols(cols),
    validator(...args) {
      const inputTypeValidator = {
        text: 'string',
        number: 'number',
        email: 'email',
      };
      return resolveValidator(
        customValidator, inputTypeValidator[inputType] || 'string', ...args,
      );
    },
    visible(...args) {
      if (isVisible) {
        return typeof isVisible === 'function' ? isVisible(...args) : isVisible;
      }

      return true;
    },
  }),

  simpleSelect: (
    label, model, required, values, noSelectionText, cols, customValidator, isVisible,
  ) => ({
    type: 'select',
    label,
    model,
    required,
    values,
    selectOptions: {
      noneSelectedText: noSelectionText || '-',
    },
    styleClasses: resolveCols(cols),
    validator(...args) {
      return resolveValidator(customValidator, 'required', ...args);
    },
    visible(...args) {
      if (isVisible) {
        return typeof isVisible === 'function' ? isVisible(...args) : isVisible;
      }

      return true;
    },
  }),

  splitAddress: (title, model, isVisible, removedFields) => ({
    type: 'splitAddress',
    title,
    model,
    visible(...args) {
      if (isVisible) {
        return typeof isVisible === 'function' ? isVisible(...args) : isVisible;
      }

      return true;
    },
    removedFields,
  }),

  address: (label, placeholder, model, required, cols, customValidator) => ({
    type: 'googleAddress',
    label,
    model,
    placeholder,
    required: true,
    styleClasses: resolveCols(cols),
    validator(...args) {
      return resolveValidator(customValidator, 'string', ...args);
    },
  }),

  password: (label, model, min, cols, customValidator) => ({
    type: 'smartPassword',
    label,
    min,
    model,
    required: true,
    styleClasses: resolveCols(cols),
    validator(...args) {
      return resolveValidator(customValidator, 'string', ...args);
    },
    placeholder: 'Password',
  }),

  date: (
    label, placeholder, model, required, cols, defaultDate,
    blockPast, blockFuture, customValidator, isVisible,
  ) => {
    const data = {
      id: `datepicker-${model}`,
      type: 'datePicker',
      label,
      model,
      required,
      styleClasses: resolveCols(cols),
      options: {
        defaultDate,
        format: 'MM/DD/YYYY',
        placeholder: placeholder || 'Select Date',
      },
      validator(...args) {
        return resolveValidator(customValidator, 'date', ...args);
      },
      visible(...args) {
        if (isVisible) {
          return typeof isVisible === 'function' ? isVisible(...args) : isVisible;
        }

        return true;
      },
    };

    if (blockPast) {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      // eslint-disable-next-line func-names
      data.options.disabledDate = function (date) {
        return date < today;
      };
    }

    if (blockFuture) {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      // eslint-disable-next-line func-names
      data.options.disabledDate = function (date) {
        return date > today;
      };
    }

    return data;
  },

  phone: (label, model, required, cols, hint) => ({
    type: 'cleave',
    label,
    model,
    required,
    hint,
    styleClasses: resolveCols(cols),
    placeholder: '(000) 000-0000',
    cleaveOptions: {
      numericOnly: true,
      blocks: [0, 3, 3, 4],
      delimiters: ['(', ') ', '-'],
    },
    pattern: /^\([0-9]{3}\) [0-9]{3}-[0-9]{4}$/g,
    validator: VueFormGenerator.validators.regexp,
  }),

  email: (label, model, placeholder, required, cols) => ({
    type: 'input',
    inputType: 'text',
    label,
    model,
    required,
    placeholder: placeholder || 'email@domain.com',
    styleClasses: resolveCols(cols),
    validator: VueFormGenerator.validators.email,
  }),

  currency: (label, model, isRequired, cols, customValidator) => ({
    type: 'currency',
    label,
    model,
    required: validBool(isRequired),
    styleClasses: resolveCols(cols),
    validator(...args) {
      return resolveValidator(customValidator, 'string', ...args);
    },
  }),

  social: (placeholder, model, network, cols, customValidator) => ({
    type: 'social',
    model,
    placeholder,
    network,
    required: false,
    styleClasses: resolveCols(cols),
    validator(...args) {
      return resolveValidator(customValidator, 'string', ...args);
    },
  }),

  ssn: (label, model, placeholder, required, cols) => ({
    type: 'SmartSsn',
    inputType: 'password',
    label,
    model,
    placeholder: placeholder || '000-00-0000',
    required,
    styleClasses: resolveCols(cols),
    pattern: /^[0-9]{3}-[0-9]{2}-[0-9]{4}$/g,
    validator: 'regexp',
  }),

  tax: (label, model, placeholder, required, cols) => ({
    type: 'cleave',
    inputType: 'text',
    label,
    model,
    placeholder: placeholder || '00-0000000',
    required,
    hint: '10-digit tax ID',
    cleaveOptions: {
      numericOnly: true,
      blocks: [2, 7],
      delimiters: ['-'],
    },
    styleClasses: resolveCols(cols),
    pattern: /^[0-9]{2}-[0-9]{7}$/g,
    validator: 'regexp',
  }),

  radios: (label, model, required, values, cols, classes, customValidator, isVisible) => ({
    type: 'radios',
    label,
    model,
    required,
    values,
    styleClasses: `${resolveCols(cols)} ${classes}`,
    visible(...args) {
      if (isVisible) {
        return typeof isVisible === 'function' ? isVisible(...args) : isVisible;
      }

      return true;
    },
    validator(...args) {
      return resolveValidator(customValidator, 'required', ...args);
    },
  }),

  boolRadio: (label, model, required, cols, customValidator, isVisible) => ({
    type: 'radios',
    label,
    model,
    required,
    styleClasses: `${resolveCols(cols)} bool-radio`,
    values: [
      { name: 'Yes', value: true },
      { name: 'No', value: false },
    ],
    visible(...args) {
      if (isVisible) {
        return typeof isVisible === 'function' ? isVisible(...args) : isVisible;
      }

      return true;
    },
    validator(...args) {
      return resolveValidator(customValidator, 'required', ...args);
    },
  }),


  switch: (label, model, required, textOn, textOff, classes, isVisible) => ({
    type: 'switch',
    label,
    model,
    textOn,
    textOff,
    required,
    styleClasses: classes || 'col-md-12',
    validator: 'required',
    visible(...args) {
      if (isVisible) {
        return typeof isVisible === 'function' ? isVisible(...args) : isVisible;
      }

      return true;
    },
  }),

  checkbox: (label, model, cols, required, classes) => ({
    type: 'fancyCheckbox',
    text: label,
    model,
    required,
    styleClasses: `${resolveCols(cols)} ${classes || ''} checkbox-pointer`,
    validator(value, field) {
      if (field.required && value !== true) {
        return ['This field is required!'];
      }
      return null;
    },
  }),

  multipleCheckbox: (label, model, options, stacked, cols, classes, required, isVisible) => ({
    type: 'multipleCheckbox',
    label,
    model,
    required,
    options,
    stacked,
    styleClasses: `${resolveCols(cols)} ${classes}`,
    validator(value, field) {
      if (!field.visible || !field.required) { return null; }

      return value && value.length > 0 ?
        null : ['This field is required!'];
    },
    visible(...args) {
      if (isVisible) {
        return typeof isVisible === 'function' ? isVisible(...args) : isVisible;
      }

      return true;
    },
  }),

  options: (prefix, constantValidation) => ({
    validationErrorClass: 'invalid-input',
    validateAfterChanged: validBool(constantValidation),
    fieldIdPrefix: prefix,
  }),
};



// WEBPACK FOOTER //
// ./src/libs/forms.js